import { createContext, useCallback, useContext, useRef, useState } from "react";
import Button from "@/components/Button";

/** Boîte de dialogue de confirmation maison (remplace window.confirm).
 *  Cohérente avec le design du site, au lieu d'une popup native du navigateur.
 *
 *  Usage :
 *    const confirm = useConfirm();
 *    if (await confirm({ title: "Supprimer ?", message: "…", danger: true })) { … }
 */
interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error("useConfirm doit être utilisé dans <ConfirmProvider>");
  return fn;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <div
          style={styles.overlay}
          onClick={() => close(false)}
          onKeyDown={(e) => { if (e.key === "Escape") close(false); }}
        >
          <div
            style={styles.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-label={opts.title}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.title}>{opts.title}</h3>
            {opts.message && <p style={styles.message}>{opts.message}</p>}
            <div style={styles.actions}>
              <Button variant="secondary" size="sm" onClick={() => close(false)} autoFocus>
                {opts.cancelLabel ?? "Annuler"}
              </Button>
              <Button
                variant={opts.danger ? "danger" : "primary"}
                size="sm"
                onClick={() => close(true)}
              >
                {opts.confirmLabel ?? "Confirmer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  dialog: {
    background: "var(--bg-panel)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-1)",
    padding: "20px 22px",
    width: "min(420px, 100%)",
    border: "1px solid var(--border)",
  },
  title: { margin: "0 0 8px", color: "var(--text)", fontSize: 16, fontWeight: 600 },
  message: { margin: "0 0 18px", color: "var(--text-muted)", fontSize: 14, lineHeight: 1.5 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8 },
};
